import type { SimEvent } from "../sim/events";

/** Keys are forgotten this many ticks after they were presented (well past any rollback). */
const MEMORY_TICKS = 600;

/**
 * Identity of an event across timelines, so a rollback's re-simulation doesn't replay a
 * sound or effect the player already saw. `null` = structural (insert/remove): always passed
 * on, the views need them to stay in sync. Destructions are keyed without the tick because
 * each entity is destroyed at most once; a corrected timeline that moves the explosion by a
 * tick or two must not show it twice. Projectile ids can differ between timelines, so shots
 * are keyed by who fired what and when.
 */
export function eventKey(ev: SimEvent, tick: number): string | null {
	switch (ev.type) {
		case "inserted":
		case "removed":
			return null;
		case "destroyed":
			return `destroyed:${ev.kind}:${ev.id}`;
		case "fired":
			return `fired:${tick}:${ev.player}:${ev.weapon}:${ev.side ? "side" : "main"}`;
		default: {
			const who = "id" in ev ? ev.id : "player" in ev ? ev.player : "";
			return `${ev.type}:${tick}:${who}`;
		}
	}
}

/**
 * Passes on every event of a newly simulated tick and, during a rollback's replay, only the
 * events the predicted timeline didn't already present. Mispredicted events that were shown
 * but didn't happen can't be taken back; the views resync their sprites separately.
 */
export class ReplayEventFilter {
	private readonly seen = new Map<string, number>();
	private prunedAt = 0;

	filter(events: readonly SimEvent[], tick: number, replay: boolean): SimEvent[] {
		const out: SimEvent[] = [];
		for (const ev of events) {
			const key = eventKey(ev, tick);
			if (key === null) {
				out.push(ev);
				continue;
			}
			const at = this.seen.get(key);
			// Seen before: skip, unless it's another event of the same new tick (both side lasers).
			if (at !== undefined && (replay || at !== tick)) continue;
			if (at === undefined) this.seen.set(key, tick);
			out.push(ev);
		}
		if (tick - this.prunedAt >= MEMORY_TICKS) {
			for (const [key, at] of this.seen) if (at < tick - MEMORY_TICKS) this.seen.delete(key);
			this.prunedAt = tick;
		}
		return out;
	}
}
